/**
 * MCP Tool Handlers for HoloScript
 *
 * Implements the logic for each MCP tool.
 * Dispatches to specialized handlers for graph, IDE, and Brittney-Lite tools.
 */

import {
  HoloScriptPlusParser,
  compilePipelineSourceToNode,
  parseHolo,
  parseHoloStrict,
  parsePipeline,
  VR_TRAITS,
  buildKnownTraitSet,
  createNativeAutoRigPlan,
  type AutoRigPose,
  type AutoRigRigType,
  type NativeAutoRigPlan,
} from '@holoscript/core';
import { validate_detailed as validateCanonicalHsDetailed } from '@holoscript/wasm/node';

import {
  suggestUniversalTraits,
  suggest2DTraits,
  suggestTraits,
  generateSemanticUIForMCP,
  generateObjectForMCP,
  generateSceneForMCP,
} from './generators';
import { generateHololandDataset, datasetToJsonl, TrainingCategory } from './training-generators';
import { renderPreview, createShareLink } from './renderer';
import { handleEditHoloTool } from './edit-holo-tools';
import { TRAIT_DOCS, SYNTAX_DOCS, EXAMPLES } from './documentation';
import { EXAMPLE_CATALOG, EXAMPLE_INVENTORY, PUBLIC_LINK_POLICIES } from './examples-catalog';
import { handleCodebaseTool, handleGraphRagTool } from '@holoscript/absorb-service/mcp';
import { handleGraphTool } from './graph-tools';
import { handleIDETool } from './ide-tools';
import { handleBrittneyLiteTool } from './brittney-lite';
import { handleWisdomGotchaTool } from './wisdom-gotcha-tools';
import { PluginManager } from './PluginManager';
import { handleAbsorbProvenanceTool } from './absorb-provenance-tools';
import {
  browserLaunch,
  browserExecute,
  browserScreenshot,
  BrowserLaunchSchema,
  BrowserExecuteSchema,
  BrowserScreenshotSchema,
} from './browser/browser-tools';
import {
  buildToolManifest,
  suggestToolsForGoal,
  handleBatchToolCall,
  handleToolingDiscoveryTool,
} from './tooling-discovery-tools';
import { handleOracleConsult } from './oracle-handler';
import { handleOracleMcpTool } from './oracle-mcp-tools';
import { isHoloMapToolName, handleHoloMapTool } from './holomap-mcp-tools';
import { isSpatialMcpToolName, handleSpatialMcpTool } from './spatial-mcp-tools';
import { isNegotiationToolName, handleNegotiationTool } from './negotiation-mcp-tools';
import { isHologramToolName, handleHologramTool } from './hologram-mcp-tools';
import { isHoloTwinToolName, handleHoloTwinTool } from './holotwin-mcp-tools';
import { isHoloTuneToolName, handleHoloTuneTool } from './holotune-mcp-tools';
import { handleEstimateTaskDurationTool } from './tools/estimate_task_duration';
import { handleKolmogorovTaskScoreTool } from './tools/kolmogorov_task_score';
import { handleCriticTool } from './critic-handler';
import { handleFounderTool } from './founder-handler';
import { handleDaemonLifecycleTool } from './daemon-lifecycle-tools';
import { handlePremortemTool } from './premortem-handler';
import {
  LEGACY_TRAIT_CATEGORY_ALIASES,
  loadTraitCategoriesFromCore,
  resolveTraitCategorySlug,
} from './trait-categories-from-core';
import type { SigningContext } from './holomesh/identity/signing-middleware';
import { runForkSandboxGate, gateHoloScriptCode } from './security/fork-sandbox-gate';
import type { CapabilityManifest } from './security/sandbox-policy';

/** Used only when core source tree is not present next to mcp-server (e.g. odd installs). */
const TRAIT_CATEGORIES_FALLBACK: Record<string, string[]> = {
  interaction: [
    '@grabbable',
    '@throwable',
    '@holdable',
    '@clickable',
    '@hoverable',
    '@draggable',
    '@pointable',
    '@scalable',
    '@rotatable',
    '@snappable',
  ],
  physics: [
    '@collidable',
    '@physics',
    '@rigid',
    '@kinematic',
    '@trigger',
    '@gravity',
    '@soft_body',
    '@fluid',
    '@magnetic',
    '@buoyant',
  ],
  visual: [
    '@glowing',
    '@emissive',
    '@transparent',
    '@reflective',
    '@animated',
    '@billboard',
    '@particle',
    '@holographic',
    '@volumetric',
    '@shader_custom',
  ],
  networking: [
    '@networked',
    '@synced',
    '@persistent',
    '@owned',
    '@host_only',
    '@replicated',
    '@authority',
    '@interpolated',
  ],
  behavior: [
    '@stackable',
    '@attachable',
    '@equippable',
    '@consumable',
    '@destructible',
    '@breakable',
    '@character',
    '@npc',
    '@pathfinding',
    '@state_machine',
  ],
  spatial: [
    '@anchor',
    '@tracked',
    '@world_locked',
    '@hand_tracked',
    '@eye_tracked',
    '@plane_detected',
    '@image_tracked',
    '@face_tracked',
  ],
  audio: [
    '@spatial_audio',
    '@ambient',
    '@voice_activated',
    '@reverb',
    '@doppler',
    '@music',
    '@procedural_audio',
  ],
  state: [
    '@state',
    '@reactive',
    '@observable',
    '@computed',
    '@event_driven',
    '@persistent_state',
    '@replicated_state',
  ],
  ai: [
    '@llm_agent',
    '@npc',
    '@crowd',
    '@reactive',
    '@pathfinding',
    '@emotion',
    '@dialogue',
    '@decision_tree',
  ],
  accessibility: [
    '@high_contrast',
    '@screen_reader',
    '@reduced_motion',
    '@voice_nav',
    '@colorblind_safe',
    '@haptic_feedback',
  ],
  iot: ['@iot_sensor', '@digital_twin', '@mqtt_bridge', '@telemetry', '@actuator', '@stream_data'],
  web3: [
    '@nft_asset',
    '@token_gated',
    '@wallet_connected',
    '@on_chain',
    '@dao_governed',
    '@smart_contract',
  ],
  advanced: [
    '@teleport',
    '@ui_panel',
    '@particle_system',
    '@weather',
    '@day_night',
    '@lod',
    '@hand_tracking',
    '@haptic',
    '@portal',
    '@mirror',
    '@ray_traced',
    '@compute_shader',
    '@lod_managed',
  ],
  social: ['@shareable', '@collaborative', '@tweetable'],
};

function getTraitCategoryMap(): Record<string, string[]> {
  const fromCore = loadTraitCategoriesFromCore();
  if (Object.keys(fromCore).length > 0) {
    const merged = { ...fromCore };
    for (const [legacy, slug] of Object.entries(LEGACY_TRAIT_CATEGORY_ALIASES)) {
      const traits = merged[slug];
      if (traits) merged[legacy] = traits;
    }
    return merged;
  }
  return TRAIT_CATEGORIES_FALLBACK;
}

function asCapabilityManifest(value: unknown): CapabilityManifest | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const candidate = value as Partial<CapabilityManifest>;
  if (
    (candidate.protocol === 'holomesh.tool_manifest.v1' ||
      candidate.protocol === 'holoscript.capability.v1') &&
    Array.isArray(candidate.declaredCapabilities)
  ) {
    return candidate as CapabilityManifest;
  }
  return undefined;
}

function validationFallbackCommand(args: Record<string, unknown>): {
  command: string[];
  commandPreview: string;
  exact: boolean;
  sourcePath: string;
} {
  const sourcePath =
    typeof args.sourcePath === 'string'
      ? args.sourcePath
      : typeof args.filePath === 'string'
        ? args.filePath
        : '';
  const target = sourcePath || '<local-holoscript-source>';
  const command = ['pnpm', 'exec', 'holoscript', 'validate', target];
  return {
    command,
    commandPreview: command.join(' '),
    exact: Boolean(sourcePath),
    sourcePath,
  };
}

function validationUnavailableReceipt(
  args: Record<string, unknown>,
  gate:
    | {
        receipt?: { receiptId: string; failedCheck: string; reason: string };
        appliedPolicy: { policyId: string };
        checks: Array<{ name: string; passed: boolean; detail?: string }>;
      }
    | undefined
) {
  return {
    schemaVersion: 'holoscript.validation-unavailable-receipt.v0.1.0',
    kind: 'ValidationUnavailableReceipt',
    generatedAt: new Date().toISOString(),
    reason: gate?.receipt?.reason ?? 'Local validation was denied before parser execution.',
    receiptId: gate?.receipt?.receiptId ?? '',
    policyId: gate?.appliedPolicy.policyId ?? '',
    failedCheck: gate?.receipt?.failedCheck ?? 'fork_sandbox_gate',
    checks: gate?.checks ?? [],
    localFallback: validationFallbackCommand(args),
    capabilityManifestTemplate: {
      protocol: 'holoscript.capability.v1',
      declaredCapabilities: ['holoscript:validate', 'filesystem:read:local-source'],
      attestation: {
        manifestHash: '<sha256-of-canonical-manifest>',
        signer: '<verified-signer>',
        trustTier: 'verified',
        attestedAt: '<iso8601>',
      },
    },
  };
}

// All 1,800+ traits from @holoscript/core
const ALL_TRAITS: readonly string[] = VR_TRAITS;

/**
 * Main handler dispatcher for all tools
 */
export async function handleTool(
  name: string,
  args: Record<string, unknown>,
  signingCtx?: SigningContext,
  // WS-1 (2026-07-02): optional override for the ForkSandboxGate subject source,
  // defaulting to 'unknown' for every existing caller (zero behavior change).
  // The ONLY intended caller of a non-default value is the new anonymous
  // consumer-generation endpoint (http-server.ts POST /api/public/generate),
  // which passes 'consumer' -- never set this from any other call site.
  subjectSourceOverride?: import('./security/sandbox-policy').SandboxSubjectSource
): Promise<unknown> {
  // ── Fork Sandbox Gate (canary task_1778618757735_zpt5) ──────────────────────────
  // Block forked/untrusted HoloScript code and sensitive tools before they
  // can touch HoloLand worlds, robot/AI substrate, payments, or player state.
  //
  // Stdio transport design (see index.ts comment at line ~284): the stdio MCP
  // transport is "trusted local process" and never carries HTTP auth headers,
  // so signingCtx is always undefined there. Bridge: if the process has a
  // valid HOLOSCRIPT_API_KEY in env (the same key that grants admin:* via the
  // OAuth2 provider) synthesize admin:* scopes so the gate honours that trust.
  // Explicit source overrides are externally-originated lanes and must not
  // inherit the stdio-local admin bridge.
  const allowLocalAdminBridge = !subjectSourceOverride && process.env['HOLOSCRIPT_API_KEY'];
  const effectiveSigningCtx: SigningContext | undefined =
    signingCtx ??
    (allowLocalAdminBridge
      ? {
          signedRequest: false,
          signingValid: true,
          signer: 'stdio-local',
          scopes: ['admin:*'],
        }
      : undefined);

  const capabilityManifest = asCapabilityManifest(args.capabilityManifest ?? args.manifest);
  const gateResult = await runForkSandboxGate(
    {
      kind: 'mcp_tool',
      source: subjectSourceOverride ?? 'unknown',
      subjectId: `mcp:${name}`,
      payload: args,
      manifest: capabilityManifest,
    },
    {
      toolName: name,
      grantedScopes: effectiveSigningCtx?.scopes ?? [],
    }
  );
  if (!gateResult.allowed) {
    return {
      success: false,
      error: `ForkSandboxGate denied tool "${name}": ${gateResult.receipt?.reason ?? 'policy violation'}`,
      receiptId: gateResult.receipt?.receiptId,
      policyId: gateResult.appliedPolicy.policyId,
      checks: gateResult.checks,
      ...(name === 'validate_holoscript'
        ? { validationUnavailableReceipt: validationUnavailableReceipt(args, gateResult) }
        : {}),
    };
  }

  // For tools that ingest HoloScript code, additionally gate the code payload
  const codePayload = args.code ?? args.content ?? args.holoscript ?? args.source;
  if (typeof codePayload === 'string' && codePayload.length > 0) {
    const codeGate = await gateHoloScriptCode(codePayload, {
      source: 'unknown',
      toolName: name,
      grantedScopes: signingCtx?.scopes ?? [],
      manifest: capabilityManifest,
    });
    if (!codeGate.allowed) {
      return {
        success: false,
        error: `ForkSandboxGate denied HoloScript payload for "${name}": ${codeGate.receipt?.reason ?? 'policy violation'}`,
        receiptId: codeGate.receipt?.receiptId,
        policyId: codeGate.appliedPolicy.policyId,
        checks: codeGate.checks,
        ...(name === 'validate_holoscript'
          ? { validationUnavailableReceipt: validationUnavailableReceipt(args, codeGate) }
          : {}),
      };
    }
  }

  // Core tools
  switch (name) {
    case 'parse_hs':
      return handleParseHs(args);
    case 'parse_holo':
      return handleParseHolo(args);
    case 'parse_pipeline':
      return handleParsePipeline(args);
    case 'compile_pipeline':
      return handleCompilePipeline(args);
    case 'validate_holoscript':
      return handleValidate(args);
    case 'list_traits':
      return handleListTraits(args);
    case 'explain_trait':
      return handleExplainTrait(args);
    case 'suggest_traits':
      return handleSuggestTraits(args);
    case 'suggest_universal_traits':
      return handleSuggestUniversalTraits(args);
    case 'suggest_2d_traits':
      return suggest2DTraits(args.description as string, args.context as string);
    case 'generate_object':
      return handleGenerateObject(args);
    case 'generate_scene':
      return handleGenerateScene(args);
    case 'generate_semantic_ui': {
      const desc = args.description;
      if (typeof desc !== 'string' || !desc.trim()) {
        throw new Error('generate_semantic_ui: description is required (non-empty string)');
      }
      return generateSemanticUIForMCP(desc, args);
    }
    case 'get_syntax_reference':
      return handleGetSyntaxReference(args);
    case 'get_examples':
      return handleGetExamples(args);
    case 'explain_code':
      return handleExplainCode(args);
    case 'analyze_code':
      return handleAnalyzeCode(args);
    case 'render_preview':
      return handleRenderPreview(args);
    case 'create_share_link':
      return handleCreateShareLink(args);
    case 'convert_format':
      return handleConvertFormat(args);
    case 'edit_holo': {
      const result = await handleEditHoloTool(name, args);
      if (result !== null) return result;
      throw new Error(
        `[edit_holo] Handler returned null for tool '${name}' with args: ${JSON.stringify(args).slice(0, 200)}. The edit_holo handler could not process this input — check that the .holo source is valid and the edit operation is supported.`
      );
    }

    // Browser control tools
    case 'browser_launch':
      return browserLaunch(BrowserLaunchSchema.parse(args));
    case 'browser_execute':
      return browserExecute(BrowserExecuteSchema.parse(args));
    case 'browser_screenshot':
      return browserScreenshot(BrowserScreenshotSchema.parse(args));
    case 'get_tool_manifest':
    case 'suggest_tools_for_goal':
    case 'batch_tool_call':
    case 'get_tool_health': {
      const { tools: allTools } = await import('./tools');
      const result = await handleToolingDiscoveryTool(name, args, allTools, (toolName, toolArgs) =>
        handleTool(toolName, toolArgs, signingCtx)
      );
      if (result !== null) return result;
      break;
    }
    case 'holo_oracle_consult':
      return handleOracleConsult(args);
  }

  // Advanced Oracle MCP tools (graduated from /oracle skill)
  if (name.startsWith('holo_oracle_') && name !== 'holo_oracle_consult') {
    const result = await handleOracleMcpTool(name, args);
    if (result !== null) return result;
  }

  // Absorb provenance wrapper tool
  if (name === 'absorb_provenance_answer') {
    const result = await handleAbsorbProvenanceTool(name, args);
    if (result !== null) return result;
  }

  // Protocol tools (HoloScript-as-Protocol) — must come before general holo_ prefix
  if (name.startsWith('holo_protocol_')) {
    const { handleProtocolTool } = await import('./protocol-tools');
    return handleProtocolTool(name, args);
  }

  // Hologram Media Pipeline MCP tools (must run before generic holo_ graph routing)
  if (isHologramToolName(name)) {
    return handleHologramTool(name, args);
  }

  // HoloTwin IoT → Hologram pipeline tools
  if (isHoloTwinToolName(name)) {
    return handleHoloTwinTool(name, args);
  }

  // HoloTune sovereign per-agent fine-tune surface
  if (isHoloTuneToolName(name)) {
    return handleHoloTuneTool(name, args);
  }

  if (name === 'holo_estimate_task_duration') {
    const result = await handleEstimateTaskDurationTool(name, args);
    if (result !== null) return result;
  }

  if (name === 'holo_task_kolmogorov_score') {
    const result = await handleKolmogorovTaskScoreTool(name, args);
    if (result !== null) return result;
  }

  if (name === 'holo_critic') {
    return handleCriticTool(args);
  }

  if (name === 'holo_founder') {
    return handleFounderTool(args);
  }

  if (name === 'holo_premortem') {
    return handlePremortemTool(args);
  }

  if (name.startsWith('holo_secrets_')) {
    const { handleSecretsBrokerTool } = await import('./secrets-broker-handler');
    const result = await handleSecretsBrokerTool(name, args, effectiveSigningCtx);
    if (result !== null) return result;
  }

  if (name === 'holo_service_scaffold') {
    const { handleObservabilityTool } = await import('./observability-tools');
    return handleObservabilityTool(name, args);
  }

  // Codebase graph tools (absorb_repo/graph_status/query_codebase) and GraphRAG
  // tools (semantic_search/ask_codebase) live in @holoscript/absorb-service.
  // index.ts's O(1) TOOL_DISPATCH_REGISTRY routes these correctly for the real
  // request path (registerCategory(codebaseTools, handleCodebaseTool) /
  // registerCategory(graphRagTools, handleGraphRagTool)), but this standalone
  // handleTool() function is also called directly by get_tool_health's probe
  // (see tooling-discovery-tools.ts) and previously had no branch for these
  // names, so they fell through to the holo_ catch-all below and threw
  // "Unknown graph tool" from handleGraphTool's switch (task_1783009395572_q3ro,
  // root-caused from task_1782886422455_jken). Mirror index.ts's wiring here so
  // both dispatch paths agree.
  if (
    name === 'holo_absorb_repo' ||
    name === 'holo_graph_status' ||
    name === 'holo_query_codebase'
  ) {
    return handleCodebaseTool(name, args);
  }

  if (name === 'holo_semantic_search' || name === 'holo_ask_codebase') {
    return handleGraphRagTool(name, args);
  }

  // All remaining holo_ tools go to the Graph tool handler
  // (Oracle, Codebase, and Wisdom/Gotcha are now handled directly via the O(1) registry in index.ts)
  if (name.startsWith('holo_')) {
    return handleGraphTool(name, args);
  }

  // IDE tools (migrated from legacy HoloLand IDE tooling)
  if (name.startsWith('hs_') && !name.startsWith('hs_ai_')) {
    return handleIDETool(name, args);
  }

  // Brittney-Lite AI tools
  if (name.startsWith('hs_ai_')) {
    return handleBrittneyLiteTool(name, args);
  }

  // Text-to-3D pipeline
  if (name === 'generate_3d_object') {
    return handleGenerate3DObject(args);
  }

  // GPU fleet world rendering
  if (name === 'render_world_on_fleet') {
    const { handleWorldRenderTool } = await import('./world-render-tools');
    return handleWorldRenderTool(name, args);
  }

  // HoloLand MCP tools (world CRUD + MMO + Twin Earth)
  if (
    name === 'generate_world_from_prompt' ||
    name === 'generate_world' ||
    name === 'create_world' ||
    name === 'get_world' ||
    name === 'update_world' ||
    name === 'delete_world' ||
    name === 'list_worlds' ||
    name === 'create_shard' ||
    name === 'get_shard' ||
    name === 'update_shard' ||
    name === 'delete_shard' ||
    name === 'list_shards' ||
    name === 'create_zone' ||
    name === 'get_zone' ||
    name === 'update_zone' ||
    name === 'delete_zone' ||
    name === 'list_zones' ||
    name === 'create_place' ||
    name === 'get_place' ||
    name === 'update_place' ||
    name === 'delete_place' ||
    name === 'list_places' ||
    name === 'create_location_quest' ||
    name === 'get_location_quest' ||
    name === 'update_location_quest' ||
    name === 'delete_location_quest' ||
    name === 'list_location_quests' ||
    name === 'hololand_shard_status' ||
    name === 'hololand_publish_zone' ||
    name === 'hololand_create_geo_anchor' ||
    name === 'hololand_get_geo_anchor' ||
    name === 'hololand_list_geo_anchors' ||
    name === 'hololand_steward_tick' ||
    name === 'hololand_capture_runtime_receipt' ||
    name === 'hololand_create_npc' ||
    name === 'hololand_get_npc' ||
    name === 'hololand_update_npc' ||
    name === 'hololand_delete_npc' ||
    name === 'hololand_list_npcs' ||
    name === 'hololand_npc_generate_dialogue' ||
    name === 'hololand_npc_byok_status' ||
    name === 'hololand_brittney_npc_mode' ||
    name === 'hololand_twin_earth_contract' ||
    name === 'hololand_twin_earth_substrate_status' ||
    // Player / Creator / Agent Provisioning (task_1778617298562_qdpb)
    name === 'hololand_provision_player' ||
    name === 'hololand_get_player' ||
    name === 'hololand_list_players' ||
    name === 'hololand_revoke_player' ||
    name === 'hololand_create_player_invite' ||
    name === 'hololand_get_invite' ||
    name === 'hololand_provision_creator' ||
    name === 'hololand_get_creator' ||
    name === 'hololand_list_creators' ||
    name === 'hololand_revoke_creator' ||
    name === 'hololand_provision_agent' ||
    name === 'hololand_get_agent' ||
    name === 'hololand_list_agents' ||
    name === 'hololand_revoke_agent' ||
    // Twin Earth Robot / AI Sovereign Tool Family (task_1778618552503_a6rb)
    name === 'twin_earth_register_identity' ||
    name === 'twin_earth_get_identity' ||
    name === 'twin_earth_update_identity' ||
    name === 'twin_earth_revoke_identity' ||
    name === 'twin_earth_list_identities' ||
    name === 'twin_earth_create_safety_envelope' ||
    name === 'twin_earth_get_safety_envelope' ||
    name === 'twin_earth_update_safety_envelope' ||
    name === 'twin_earth_delete_safety_envelope' ||
    name === 'twin_earth_list_safety_envelopes' ||
    name === 'twin_earth_grant_permission' ||
    name === 'twin_earth_revoke_permission' ||
    name === 'twin_earth_validate_permission' ||
    name === 'twin_earth_list_permissions' ||
    name === 'twin_earth_robot_actuate' ||
    name === 'twin_earth_ai_invoke' ||
    name === 'twin_earth_capture_receipt' ||
    // Conformance Artifact Admission Gate (task_1778618757735_q298)
    name === 'conformance_check_artifact' ||
    name === 'conformance_admit_artifact' ||
    name === 'conformance_list_rules'
  ) {
    const { handleHololandMcpTool } = await import('./hololand-mcp-tools');
    return handleHololandMcpTool(name, args);
  }

  // Hololand training data generation
  if (name === 'generate_hololand_training') {
    return handleGenerateHololandTraining(args);
  }

  // Service contract tools
  if (name === 'generate_service_contract' || name === 'explain_service_contract') {
    const { handleServiceContractTool } = await import('./service-contract-tools');
    return handleServiceContractTool(name, args);
  }

  // Fairness tools — verifiable algorithmic-fairness sweep + receipt
  if (
    name === 'fairness_sweep' ||
    name === 'explain_fairness_receipt' ||
    name === 'compile_to_bias_audit_report'
  ) {
    const { handleFairnessTool } = await import('./fairness-mcp-tools');
    return handleFairnessTool(name, args);
  }

  // Composition validation tool
  if (name === 'validate_composition') {
    const { handleValidationTool } = await import('./validation-tools');
    return handleValidationTool(name, args);
  }

  // Code health analysis tool
  if (name === 'holoscript_code_health') {
    const { handleCodeHealthTool } = await import('./code-health-tools');
    return handleCodeHealthTool(name, args);
  }

  // Simulation tools with CAEL trace metadata
  if (
    name === 'solve_structural' ||
    name === 'solve_thermal' ||
    name === 'verify_cael_trace' ||
    name === 'solve_logic' ||
    name === 'train_rom' ||
    name === 'compile_to_rom_twin'
  ) {
    const { handleSimulationTool } = await import('./simulation-tools');
    return handleSimulationTool(name, args);
  }

  // Simulation billing tools (paid, capped solver execution via fleet)
  if (name === 'sim_quote' || name === 'sim_run_paid' || name === 'sim_fleet_status') {
    const { handleSimulationBillingTool } = await import('./simulation-billing-tools');
    return handleSimulationBillingTool(name, args);
  }

  // TypeScript absorb tools
  if (name === 'absorb_typescript' || name === 'absorb_suggest_holoscript_transform') {
    const { handleAbsorbTypescriptTool } = await import('@holoscript/absorb-service/mcp');
    return handleAbsorbTypescriptTool(name, args);
  }

  // Agent orchestration tools
  if (
    name === 'discover_agents' ||
    name === 'delegate_task' ||
    name === 'get_task_status' ||
    name === 'compose_workflow' ||
    name === 'execute_workflow' ||
    name === 'workflow_memory_write' ||
    name === 'workflow_memory_read' ||
    name === 'workflow_memory_subscribe'
  ) {
    const { handleAgentOrchestrationTool } = await import('./agent-orchestration-tools');
    // Wire a real tool executor so execute_workflow steps actually invoke MCP tools
    // (previously every step returned a dry-run stub). Steps run with the caller's
    // signing context; guard against execute_workflow re-entering itself.
    const workflowToolExecutor = async (
      toolName: string,
      toolArgs: Record<string, unknown>
    ): Promise<unknown> => {
      if (toolName === 'execute_workflow') {
        throw new Error('execute_workflow cannot be nested as a workflow step (recursion guard)');
      }
      return handleTool(toolName, toolArgs, effectiveSigningCtx);
    };
    return handleAgentOrchestrationTool(name, args, workflowToolExecutor);
  }

  // Agent identity tools
  if (
    name === 'issue_agent_token' ||
    name === 'verify_agent_token' ||
    name === 'check_permission' ||
    name === 'get_delegation_chain'
  ) {
    const { handleAgentIdentityTool } = await import('./agent-identity-tools');
    return handleAgentIdentityTool(name, args);
  }

  // Observability tools (v5.6)
  if (
    name === 'query_traces' ||
    name === 'export_traces_otlp' ||
    name === 'get_agent_health' ||
    name === 'get_metrics_prometheus' ||
    name === 'holo_service_scaffold'
  ) {
    const { handleObservabilityTool } = await import('./observability-tools');
    return handleObservabilityTool(name, args);
  }

  // Plugin management tools (v5.7)
  if (
    name === 'install_plugin' ||
    name === 'install_domain_plugin' ||
    name === 'discover_plugins' ||
    name === 'list_plugins' ||
    name === 'manage_plugin'
  ) {
    const { handlePluginManagementTool } = await import('./plugin-management-tools');
    return handlePluginManagementTool(name, args);
  }

  // Economy tools (v5.8 + v6.1 unified budget)
  if (
    name === 'check_agent_budget' ||
    name === 'get_usage_summary' ||
    name === 'get_creator_earnings' ||
    name === 'settle_creator_payout' ||
    name === 'optimize_scene_budget' ||
    name === 'validate_marketplace_pricing' ||
    name === 'get_unified_budget_state'
  ) {
    const { handleEconomyTool } = await import('./economy-tools');
    return handleEconomyTool(name, args);
  }

  // Developer tools (v5.9 + holo_generate_bindings)
  if (
    name === 'get_api_reference' ||
    name === 'serve_preview' ||
    name === 'get_workspace_info' ||
    name === 'inspect_trace_waterfall' ||
    name === 'get_dev_dashboard_state' ||
    name === 'holo_generate_bindings'
  ) {
    const { handleDeveloperTool } = await import('./developer-tools');
    return handleDeveloperTool(name, args);
  }

  // HoloMesh spatial mesh tools
  if (name.startsWith('holomesh_')) {
    const { handleHoloMeshTool } = await import('./holomesh/index');
    return handleHoloMeshTool(name, args);
  }

  // ConversationDaemon lifecycle tools (D.052 Brittney field / user daemon model)
  if (
    name === 'holo_create_daemon' ||
    name === 'holo_get_daemon' ||
    name === 'holo_update_daemon_ritual' ||
    name === 'holo_list_daemons'
  ) {
    const result = await handleDaemonLifecycleTool(name, args);
    if (result !== null) return result;
  }

  // Trait composition / ROS2 sync / economic contract (trait-tools.ts) — also in index.ts registry
  if (
    name === 'compile_trait_composition' ||
    name === 'sync_hardware_loop' ||
    name === 'execute_economic_contract'
  ) {
    const { handleTraitTool } = await import('./trait-tools');
    const traitResult = await handleTraitTool(name, args);
    if (traitResult !== null) {
      return traitResult;
    }
  }

  // Handle plugins
  const pluginResult = await PluginManager.handleTool(name, args);
  if (pluginResult !== null) {
    return pluginResult;
  }

  // HoloMap reconstruction tools
  if (isHoloMapToolName(name)) {
    return handleHoloMapTool(name, args);
  }

  // Spatial MCP - compile_to_spatial (v0.1, research/2026-05-07_spatial-mcp-spec.md)
  if (isSpatialMcpToolName(name)) {
    return handleSpatialMcpTool(name, args);
  }

  // Agent negotiation primitives (task_1778175122619_xk5l) — wraps xsp6 / qe2i
  // pure logic in MCP-shape adapters so external agents can drive the
  // request_quote → quote → accept → execute → settle / dispute cycle via MCP.
  if (isNegotiationToolName(name)) {
    return handleNegotiationTool(name, args);
  }

  // Skeleton interop — rig_match_skeleton (G4)
  if (name === 'rig_match_skeleton') {
    const { handleRigMatchSkeleton } = await import('./tools/rig_match_skeleton');
    return handleRigMatchSkeleton(args as unknown as Parameters<typeof handleRigMatchSkeleton>[0]);
  }

  // HoloShell download-recovery tools (local shelf custody) — dispatch to the handler objects.
  if (name.startsWith('holoshell_download_recovery_')) {
    const { holoshellDownloadRecoveryTools } = await import('./holoshell-download-recovery-tools');
    const tool = holoshellDownloadRecoveryTools.find((t) => t.name === name);
    if (tool) return tool.handler(args);
  }

  throw new Error(`Unknown tool: ${name}`);
}

// === PARSING HANDLERS ===

async function handleParseHs(args: Record<string, unknown>) {
  const code = args.code as string;
  const _format = (args.format as string) || 'hsplus';

  try {
    const parser = new HoloScriptPlusParser();
    const result = parser.parse(code);
    const errors = result.errors || [];
    const warnings = result.warnings || [];
    const success = result.success === true && errors.length === 0;

    // Deduplicate AST: root-level 'body' duplicates 'children'
    const ast = result.ast;
    if (ast && typeof ast === 'object' && 'body' in ast && 'children' in ast) {
      const { body: _body, ...cleanAst } = ast as Record<string, unknown>;
      return {
        success,
        ast: cleanAst,
        errors,
        warnings,
        ...(args.includeSourceMap
          ? { sourceMap: (result as unknown as Record<string, unknown>).sourceMap }
          : {}),
      };
    }

    return {
      success,
      ast,
      errors,
      warnings,
      ...(args.includeSourceMap
        ? { sourceMap: (result as unknown as Record<string, unknown>).sourceMap }
        : {}),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

async function handleParseHolo(args: Record<string, unknown>) {
  const code = args.code as string;
  const strict = args.strict as boolean;

  try {
    const result = strict ? parseHoloStrict(code) : parseHolo(code);
    const errors = ('errors' in result ? result.errors : []) || [];
    const parseSuccess = 'success' in result ? result.success !== false : true;

    return {
      success: parseSuccess && errors.length === 0,
      composition: result,
      errors,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

async function handleParsePipeline(args: Record<string, unknown>) {
  const code = args.code as string;

  try {
    if (typeof code !== 'string') {
      return { success: false, error: 'Missing required string argument: code' };
    }

    const result = parsePipeline(code);
    return {
      success: result.success,
      pipeline: result.pipeline,
      errors: result.errors,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

async function handleCompilePipeline(args: Record<string, unknown>) {
  const code = args.code as string;
  const target = (args.target as string) || 'node';
  const moduleName = (args.moduleName as string) || 'index.mjs';

  if (target !== 'node') {
    return {
      success: false,
      error: `Unsupported target: ${target}. Currently only 'node' is available.`,
    };
  }

  if (typeof code !== 'string') {
    return { success: false, error: 'Missing required string argument: code' };
  }

  const result = compilePipelineSourceToNode(code, { moduleName });
  if (!result.success) {
    return {
      success: false,
      errors: result.errors || [],
      error: (result.errors || []).join('; ') || 'Pipeline compilation failed',
    };
  }

  return {
    success: true,
    code: result.code,
  };
}

// === VALIDATION HANDLER ===

async function handleValidate(args: Record<string, unknown>) {
  const codeInput = args.code ?? args.content;
  const format = (args.format as string) || 'auto';
  const includeWarnings = args.includeWarnings !== false;
  const includeSuggestions = args.includeSuggestions !== false;

  if (typeof codeInput !== 'string') {
    return {
      valid: false,
      format,
      errors: [
        {
          code: 'missing-code',
          line: 1,
          message:
            'validate_holoscript requires a string `code` argument. `content` is also accepted as a compatibility alias.',
          suggestion:
            'Call validate_holoscript with { "code": "<HoloScript source>" } or { "content": "<HoloScript source>" }.',
        },
      ],
      summary: 'Missing HoloScript source',
    };
  }

  const code = codeInput;

  try {
    // Detect format if auto
    const detectedFormat = format === 'auto' ? detectFormat(code) : format;

    // Parse based on format
    let parseResult;
    let validator: 'holo-parser' | 'rust-wasm' | 'typescript-hsplus';
    if (detectedFormat === 'holo') {
      parseResult = parseHolo(code);
      validator = 'holo-parser';
    } else if (detectedFormat === 'hs') {
      parseResult = JSON.parse(validateCanonicalHsDetailed(code)) as {
        valid: boolean;
        errors: Array<{ message: string; line?: number; column?: number }>;
        warnings?: Array<{ message: string; line?: number; column?: number }>;
      };
      validator = 'rust-wasm';
    } else {
      const parser = new HoloScriptPlusParser();
      parseResult = parser.parse(code);
      validator = 'typescript-hsplus';
    }

    const errors: AIFriendlyError[] = [];
    const warnings: AIFriendlyError[] = [];

    // Convert errors to AI-friendly format
    if (parseResult.errors) {
      for (const err of parseResult.errors) {
        const aiError = toAIFriendlyError(err, code, includeSuggestions);
        errors.push(aiError);
      }
    }

    if (includeWarnings && parseResult.warnings) {
      for (const warn of parseResult.warnings) {
        const aiWarn = toAIFriendlyError(warn, code, includeSuggestions);
        warnings.push(aiWarn);
      }
    }

    // Check for unknown traits — add as warnings, not errors
    if (includeWarnings) {
      // Use the SSOT known-trait union (VR + Native2D + code-graph + runtime/governance directive
      // traits like @freeze_when / @llama_serve), not VR_TRAITS alone — otherwise legitimate
      // top-level directive traits falsely trip the unknown-trait warning.
      const KNOWN_TRAIT_SET: Set<string> = buildKnownTraitSet();
      const traitMatches = [...code.matchAll(/@(\w+)/g)];
      for (const match of traitMatches) {
        const traitName = match[1];
        if (!KNOWN_TRAIT_SET.has(traitName)) {
          warnings.push({
            code: 'unknown-trait',
            message: `Unknown trait @${traitName} — not a known trait (${KNOWN_TRAIT_SET.size} known traits)`,
            line: code.substring(0, match.index).split('\n').length,
            suggestion: `Check available traits with list_traits tool`,
          });
        }
      }
    }

    const hasWarnings = warnings.length > 0;
    return {
      valid: errors.length === 0,
      format: detectedFormat,
      validator,
      errors,
      ...(includeWarnings && { warnings }),
      summary:
        errors.length > 0
          ? `Found ${errors.length} error(s)`
          : hasWarnings
            ? `Valid with ${warnings.length} warning(s) — review unknown traits`
            : 'Valid HoloScript code',
    };
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

// === TRAITS HANDLERS ===

async function handleListTraits(args: Record<string, unknown>) {
  const category = (args.category as string) || 'all';
  const map = getTraitCategoryMap();

  if (category === 'all') {
    const fromCore = loadTraitCategoriesFromCore();
    const coreSlugs = Object.keys(fromCore).sort();
    const categories =
      coreSlugs.length > 0
        ? Object.fromEntries(coreSlugs.map((s) => [s, fromCore[s].length]))
        : Object.fromEntries(
            Object.entries(TRAIT_CATEGORIES_FALLBACK).map(([k, v]) => [k, v.length])
          );
    return {
      total: ALL_TRAITS.length,
      categories,
      categorySlugs: coreSlugs.length > 0 ? coreSlugs : Object.keys(TRAIT_CATEGORIES_FALLBACK),
      legacyAliases: Object.keys(LEGACY_TRAIT_CATEGORY_ALIASES),
      list: ALL_TRAITS,
    };
  }

  const slug = resolveTraitCategorySlug(category);
  const traits = map[category] ?? map[slug];
  if (!traits) {
    return {
      error: `Unknown category: ${category}`,
      resolvedSlug: slug,
      validCategoriesSample: Object.keys(map).slice(0, 48),
      hint: 'Use category=all for categorySlugs and legacyAliases, or pass a core slug (e.g. core-vr-interaction).',
    };
  }

  return {
    category: slug,
    requestedAs: category,
    count: traits.length,
    traits,
  };
}

async function handleExplainTrait(args: Record<string, unknown>) {
  let trait = args.trait as string;

  // Normalize trait name
  if (!trait.startsWith('@')) {
    trait = '@' + trait;
  }

  const doc = TRAIT_DOCS[trait];
  if (!doc) {
    // Find similar traits
    const similar = findSimilarTraits(trait);
    return {
      error: `Unknown trait: ${trait}`,
      suggestion: similar.length > 0 ? `Did you mean: ${similar.join(', ')}?` : null,
      allTraits: ALL_TRAITS,
    };
  }

  return doc;
}

async function handleSuggestTraits(args: Record<string, unknown>) {
  const description = args.description as string;
  const context = args.context as string | undefined;

  return suggestTraits(description, context);
}

async function handleSuggestUniversalTraits(args: Record<string, unknown>) {
  const description = args.description as string;
  const domain = args.domain as string | undefined;
  const context = args.context as string | undefined;

  return suggestUniversalTraits(description, domain, context);
}

// === GENERATION HANDLERS ===

async function handleGenerateObject(args: Record<string, unknown>) {
  const description = args.description as string;
  const format = (args.format as string) || 'hsplus';
  const includeDocs = args.includeDocs as boolean;

  return generateObjectForMCP(description, {
    format: format as 'hs' | 'hsplus' | 'holo',
    includeDocs,
  });
}

async function handleGenerateScene(args: Record<string, unknown>) {
  const description = args.description as string;
  const style = (args.style as string) || 'detailed';
  const features = (args.features as string[]) || [];

  return generateSceneForMCP(description, {
    style: style as 'minimal' | 'detailed' | 'production',
    features,
  });
}

// === DOCUMENTATION HANDLERS ===

async function handleGetSyntaxReference(args: Record<string, unknown>) {
  const topic = args.topic as string;

  const doc = SYNTAX_DOCS[topic];
  if (!doc) {
    return {
      error: `Unknown topic: ${topic}`,
      availableTopics: Object.keys(SYNTAX_DOCS),
    };
  }

  return doc;
}

const LIST_TOKENS = new Set(['', 'list', 'all', 'catalog', 'index', '*', 'browse']);

/** The audited public example catalog (real repo state from the health matrix),
 *  ordered so working/supported examples surface before aspirational ones. */
function publicCatalog() {
  return EXAMPLE_CATALOG.filter((e) => PUBLIC_LINK_POLICIES.has(e.linkPolicy)).sort((a, b) => {
    const rank = (s: string) => (s === 'supported' ? 0 : 1);
    return rank(a.status) - rank(b.status);
  });
}

/** Browse mode — return the real catalog + full-tree inventory so callers can
 *  discover the actual repo examples instead of getting an empty/erroring result. */
function exampleCatalogView() {
  const inlineKeys = Object.keys(EXAMPLES);
  return {
    kind: 'example-catalog',
    inventory: {
      totalExampleFiles: EXAMPLE_INVENTORY.total,
      byFormat: EXAMPLE_INVENTORY.byFormat,
      categories: Object.keys(EXAMPLE_INVENTORY.byCategory).sort(),
      byCategory: EXAMPLE_INVENTORY.byCategory,
    },
    auditedExamples: publicCatalog().map((e) => ({
      slug: e.slug,
      path: e.path,
      format: e.format,
      category: e.category,
      status: e.status,
      reason: e.reason,
    })),
    inlinePatterns: inlineKeys,
    fullIndex: 'examples/INDEX.md',
    hint:
      'Pass a slug (e.g. "interactive-object"), a path ("examples/physics/..."), ' +
      'or a keyword ("physics", "robot", "teleport") to fetch a specific example. ' +
      `${EXAMPLE_INVENTORY.total} example files total; ${publicCatalog().length} are audited & link-policy-tagged here. ` +
      'Inline patterns return runnable code; catalog entries return the repo path + parser status.',
  };
}

async function handleGetExamples(args: Record<string, unknown>) {
  const raw = typeof args.pattern === 'string' ? args.pattern : '';
  const pattern = raw.trim();

  // Browse / list mode — no pattern (or an explicit list token) returns the real
  // repo catalog + inventory instead of erroring. Fixes get_examples returning 0.
  if (LIST_TOKENS.has(pattern.toLowerCase())) {
    return exampleCatalogView();
  }

  const inlineKeys = Object.keys(EXAMPLES);

  // 1. Exact inline match — richest result (runnable code).
  const example = EXAMPLES[pattern];
  if (example) return example;

  // 2. Exact catalog match by slug or path.
  const lower = pattern.toLowerCase();
  const catalogExact = EXAMPLE_CATALOG.find(
    (e) => e.slug.toLowerCase() === lower || e.path.toLowerCase() === lower
  );
  if (catalogExact) {
    return { kind: 'example-ref', ...catalogExact };
  }

  // 3. Fuzzy match across BOTH inline patterns and the real catalog.
  const queryWords = lower
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const overlaps = (slug: string) => {
    const slugWords = slug
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
    return queryWords.filter((q) => slugWords.some((s) => s.includes(q) || q.includes(s))).length;
  };

  const inlineScored = inlineKeys
    .map((k) => ({ key: k, score: overlaps(k) }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);

  if (inlineScored.length > 0) {
    return { ...EXAMPLES[inlineScored[0].key], _matchedPattern: inlineScored[0].key };
  }

  const catalogScored = EXAMPLE_CATALOG.map((e) => ({
    entry: e,
    score: Math.max(overlaps(e.slug), overlaps(e.category), overlaps(e.path)),
  }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  if (catalogScored.length > 0) {
    return {
      kind: 'example-matches',
      query: pattern,
      matches: catalogScored.map((s) => ({
        slug: s.entry.slug,
        path: s.entry.path,
        format: s.entry.format,
        status: s.entry.status,
      })),
      hint: 'These are repo example paths. Open via holo_read_file / the repo, or refine the query.',
    };
  }

  // 4. Nothing matched — return the catalog so the caller can still discover.
  return {
    error: `No example matched: ${pattern}`,
    ...exampleCatalogView(),
  };
}

async function handleExplainCode(args: Record<string, unknown>) {
  const code = args.code as string;
  const detail = (args.detail as string) || 'detailed';

  // Parse the code first
  const format = detectFormat(code);
  let parsed;

  try {
    if (format === 'holo') {
      parsed = parseHolo(code);
    } else {
      const parser = new HoloScriptPlusParser();
      parsed = parser.parse(code);
    }
  } catch (error) {
    return {
      error: 'Failed to parse code',
      parseError: error instanceof Error ? error.message : String(error),
    };
  }

  // Generate explanation based on AST
  const explanation = generateExplanation(parsed, detail);

  return {
    format,
    explanation,
    detail,
  };
}

async function handleAnalyzeCode(args: Record<string, unknown>) {
  const code = args.code as string;
  const format = detectFormat(code);

  let parsed;
  try {
    if (format === 'holo') {
      parsed = parseHolo(code);
    } else {
      const parser = new HoloScriptPlusParser();
      parsed = parser.parse(code);
    }
  } catch (error) {
    return {
      error: 'Failed to parse code',
      parseError: error instanceof Error ? error.message : String(error),
    };
  }

  return analyzeAST(parsed, code);
}

// === RENDERING HANDLERS ===

async function handleRenderPreview(args: Record<string, unknown>) {
  return renderPreview({
    code: args.code as string,
    format: ((args.format as string) || 'png') as 'png' | 'gif' | 'mp4' | 'webp',
    resolution: (args.resolution as number[]) || [800, 600],
    camera: args.camera as { position?: number[]; target?: number[] },
    duration: args.duration as number,
    quality: ((args.quality as string) || 'preview') as 'draft' | 'preview' | 'production',
  });
}

async function handleCreateShareLink(args: Record<string, unknown>) {
  return createShareLink({
    code: args.code as string,
    title: args.title as string,
    description: args.description as string,
    platform: ((args.platform as string) || 'x') as 'x' | 'generic' | 'codesandbox' | 'stackblitz',
  });
}

// === CONVERSION HANDLER ===

async function handleConvertFormat(args: Record<string, unknown>) {
  const code = args.code as string;
  const from = args.from as string;
  const to = args.to as string;

  try {
    const convertedCode = convertFormat(code, from, to);
    return {
      success: true,
      original: from,
      target: to,
      code: convertedCode,
      note: 'Format conversion is a best-effort process. Manual review recommended.',
    };
  } catch (error) {
    return {
      success: false,
      original: from,
      target: to,
      code: code,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Convert between HoloScript formats (hs, hsplus, holo)
 */
function convertFormat(code: string, from: string, to: string): string {
  // First, parse the source format
  let ast: any;

  if (from === 'holo') {
    ast = parseHolo(code);
  } else {
    // HoloScriptPlusParser handles both .hs and .hsplus formats
    const parser = new HoloScriptPlusParser();
    const result = parser.parse(code);
    ast = result.ast;
  }

  // Convert AST to target format
  if (to === 'holo') {
    return convertToHolo(ast, from);
  } else if (to === 'hsplus') {
    return convertToHsPlus(ast, from);
  } else {
    return convertToHs(ast, from);
  }
}

function convertToHolo(ast: any, _from: string): string {
  const lines: string[] = [];
  lines.push('composition "Converted Scene" {');

  // Add environment block
  lines.push('  environment {');
  lines.push('    skybox: "nebula"');
  lines.push('    ambient_light: 0.4');
  lines.push('  }');
  lines.push('');

  // Extract objects from AST
  const objects = extractObjects(ast);

  for (const obj of objects) {
    const traits = obj.traits?.length
      ? obj.traits.map((t: string) => `@${t.replace('@', '')}`).join(' ')
      : '';
    lines.push(`  object "${obj.name || 'obj'}" ${traits} {`.trim() + ' {');
    lines.push(`    geometry: "${obj.type || 'cube'}"`);
    if (obj.position) {
      lines.push(`    position: [${obj.position.join(', ')}]`);
    }
    if (obj.color) {
      lines.push(`    color: "${obj.color}"`);
    }
    lines.push('  }');
  }

  lines.push('}');
  return lines.join('\n');
}

function convertToHsPlus(ast: any, _from: string): string {
  const lines: string[] = [];
  const objects = extractObjects(ast);

  for (const obj of objects) {
    const traits = obj.traits?.length
      ? obj.traits.map((t: string) => `  @${t.replace('@', '')}`).join('\n')
      : '';
    lines.push(`${obj.type || 'object'} ${obj.name || 'obj'} {`);
    if (traits) lines.push(traits);
    if (obj.position) {
      lines.push(`  position: [${obj.position.join(', ')}]`);
    }
    if (obj.color) {
      lines.push(`  color: "${obj.color}"`);
    }
    lines.push('}');
    lines.push('');
  }

  return lines.join('\n').trim();
}

function convertToHs(ast: any, _from: string): string {
  const lines: string[] = [];
  const objects = extractObjects(ast);

  for (const obj of objects) {
    lines.push(`${obj.type || 'orb'} ${obj.name || 'obj'} {`);
    if (obj.position) {
      lines.push(
        `  position: [${obj.position[0] || 0}, ${obj.position[1] || 0}, ${obj.position[2] || 0}]`
      );
    }
    if (obj.color) {
      lines.push(`  color: "${obj.color}"`);
    }
    lines.push('}');
    lines.push('');
  }

  return lines.join('\n').trim();
}

function extractObjects(ast: any): any[] {
  const objects: any[] = [];

  if (!ast) return objects;

  // Handle .holo composition format
  if (ast.objects && Array.isArray(ast.objects)) {
    for (const obj of ast.objects) {
      objects.push({
        name: obj.name || obj.id,
        type: obj.type || obj.geometry || 'cube',
        position: obj.position,
        color: obj.color,
        traits: obj.traits || [],
      });
    }
  }

  // Handle .hsplus or .hs format
  if (ast.nodes && Array.isArray(ast.nodes)) {
    for (const node of ast.nodes) {
      if (node.type === 'object' || node.nodeType === 'object') {
        objects.push({
          name: node.name || node.id,
          type: node.objectType || node.geometry || 'cube',
          position: node.props?.position || node.position,
          color: node.props?.color || node.color,
          traits: node.traits || [],
        });
      }
    }
  }

  // Handle flat declaration list
  if (ast.declarations && Array.isArray(ast.declarations)) {
    for (const decl of ast.declarations) {
      objects.push({
        name: decl.name,
        type: decl.type || 'orb',
        position: decl.props?.position,
        color: decl.props?.color,
        traits: [],
      });
    }
  }

  return objects;
}

// === HELPER FUNCTIONS ===

interface AIFriendlyError {
  code: string;
  line: number;
  column?: number;
  message: string;
  context?: string;
  suggestion?: string;
  fix?: {
    type: 'replace' | 'insert' | 'delete';
    old?: string;
    new?: string;
    position?: number;
  };
}

function detectFormat(code: string): 'hs' | 'hsplus' | 'holo' {
  if (code.includes('composition') && code.includes('{')) {
    return 'holo';
  }
  if (code.includes('@') || code.includes('state {')) {
    return 'hsplus';
  }
  return 'hs';
}

function toAIFriendlyError(
  error: { message: string; line?: number; column?: number },
  code: string,
  includeSuggestions: boolean
): AIFriendlyError {
  const message = error.message;
  const line = error.line || 1;

  const aiError: AIFriendlyError = {
    code: extractErrorCode(message),
    line,
    column: error.column,
    message,
  };

  // Add context from source
  const lines = code.split('\n');
  if (line > 0 && line <= lines.length) {
    aiError.context = lines[line - 1].trim();
  }

  // Add suggestions if enabled
  if (includeSuggestions) {
    const suggestion = generateSuggestion(message);
    if (suggestion) {
      aiError.suggestion = suggestion.message;
      aiError.fix = suggestion.fix;
    }
  }

  return aiError;
}

function extractErrorCode(message: string): string {
  const canonical = message.match(/\b(HS(?:P)?\d{3})\b/);
  if (canonical) return canonical[1];

  // Extract error code from message if present
  const match = message.match(/\[(E\d+|W\d+)\]/);
  if (match) return match[1];

  // Generate a code based on error type
  if (message.includes('Unknown trait')) return 'E001';
  if (message.includes('syntax')) return 'E002';
  if (message.includes('unexpected')) return 'E003';
  if (message.includes('missing')) return 'E004';
  return 'E999';
}

function generateSuggestion(
  message: string
): { message: string; fix?: AIFriendlyError['fix'] } | null {
  // Unknown trait
  const traitMatch = message.match(/Unknown trait:?\s*[@]?(\w+)/i);
  if (traitMatch) {
    const trait = '@' + traitMatch[1];
    const similar = findSimilarTraits(trait);
    if (similar.length > 0) {
      return {
        message: `Did you mean ${similar[0]}?`,
        fix: { type: 'replace', old: trait, new: similar[0] },
      };
    }
  }

  // Typo in geometry
  const geoMatch = message.match(/(spher|cub|cylinder|con|plan)/i);
  if (geoMatch) {
    const corrections: Record<string, string> = {
      sper: 'sphere',
      spher: 'sphere',
      cub: 'cube',
      con: 'cone',
      plan: 'plane',
    };
    const key = geoMatch[1].toLowerCase();
    if (corrections[key]) {
      return {
        message: `Did you mean '${corrections[key]}'?`,
        fix: { type: 'replace', old: geoMatch[0], new: corrections[key] },
      };
    }
  }

  return null;
}

function findSimilarTraits(trait: string): string[] {
  const normalized = trait.replace('@', '').toLowerCase();

  return ALL_TRAITS.filter((t) => {
    const tName = t.replace('@', '').toLowerCase();
    // Simple Levenshtein-like matching
    if (tName.includes(normalized) || normalized.includes(tName)) return true;
    // Check first few chars
    if (tName.substring(0, 3) === normalized.substring(0, 3)) return true;
    return false;
  }).slice(0, 3);
}

interface ParsedAST {
  composition?: unknown;
  type?: string;
  name?: string;
  nodes?: Array<{
    type?: string;
    nodeType?: string;
    name?: string;
    id?: string;
    objectType?: string;
    geometry?: string;
    traits?: string[];
  }>;
  ast?: { nodes?: ParsedAST['nodes'] };
  objects?: Array<{
    name?: string;
    id?: string;
    type?: string;
    geometry?: string;
    traits?: string[];
  }>;
  environment?: { skybox?: string; ambient_light?: number; theme?: string };
  logic?: unknown;
  actions?: unknown;
}

function generateExplanation(parsed: unknown, detail: string): string {
  const ast = parsed as ParsedAST;
  const sections: string[] = [];

  // Overview section
  sections.push('## Overview');

  // Detect format and describe structure
  if (ast?.composition || ast?.type === 'composition') {
    sections.push('This is a **.holo** declarative composition file.');
    if (ast.name) {
      sections.push(`Composition name: **${ast.name}**`);
    }
  } else if (ast?.nodes || ast?.ast?.nodes) {
    const nodes = ast.nodes || ast.ast?.nodes;
    sections.push('This is a **.hsplus** (HoloScript Plus) file with VR traits.');
    sections.push(`Contains **${nodes?.length || 0}** top-level declarations.`);
  } else {
    sections.push('This is a **.hs** (Classic HoloScript) file.');
  }

  // Environment description
  if (ast?.environment) {
    sections.push('');
    sections.push('### Environment');
    const env = ast.environment;
    if (env.skybox) sections.push(`- Skybox: **${env.skybox}**`);
    if (env.ambient_light !== undefined) sections.push(`- Ambient light: **${env.ambient_light}**`);
    if (env.theme) sections.push(`- Theme: **${env.theme}**`);
  }

  // Objects description
  const objects = extractObjectsForExplanation(ast);
  if (objects.length > 0) {
    sections.push('');
    sections.push('### Objects');

    if (detail === 'brief') {
      sections.push(`Contains **${objects.length}** objects.`);
    } else {
      for (const obj of objects.slice(0, 10)) {
        let desc = `- **${obj.name}**`;
        if (obj.type) desc += ` (${obj.type})`;
        if (obj.traits?.length) desc += ` with traits: ${obj.traits.join(', ')}`;
        sections.push(desc);
      }
      if (objects.length > 10) {
        sections.push(`... and **${objects.length - 10}** more objects.`);
      }
    }
  }

  // Traits summary
  const allTraits = new Set<string>();
  for (const obj of objects) {
    for (const trait of obj.traits || []) {
      allTraits.add(trait);
    }
  }
  if (allTraits.size > 0) {
    sections.push('');
    sections.push('### VR Traits Used');
    sections.push(Array.from(allTraits).join(', '));
  }

  // Logic/behavior summary
  if (ast?.logic || ast?.actions) {
    sections.push('');
    sections.push('### Behavior');
    sections.push('Contains event handlers and/or action definitions for interactivity.');
  }

  return sections.join('\n');
}

interface ExtractedObject {
  name: string;
  type?: string;
  traits: string[];
}

function extractObjectsForExplanation(ast: ParsedAST): ExtractedObject[] {
  const objects: ExtractedObject[] = [];

  if (!ast) return objects;

  // Handle .holo format
  if (ast.objects && Array.isArray(ast.objects)) {
    for (const obj of ast.objects) {
      objects.push({
        name: obj.name || obj.id || 'unnamed',
        type: obj.type || obj.geometry,
        traits: obj.traits || [],
      });
    }
  }

  // Handle .hsplus/.hs format
  const nodes = ast.nodes || ast.ast?.nodes || [];
  for (const node of nodes) {
    if (node.type === 'object' || node.nodeType === 'object') {
      objects.push({
        name: node.name || node.id || 'unnamed',
        type: node.objectType || node.geometry,
        traits: node.traits || [],
      });
    }
  }

  return objects;
}

function analyzeAST(parsed: unknown, code: string) {
  const lines = code.split('\n').length;
  const objects = (code.match(/\b(orb|object|cube|sphere|model)\s+\w+/gi) || []).length;
  const traits = (code.match(/@\w+/g) || []).length;
  const functions = (code.match(/\b(function|action|on_\w+)\s*\(/gi) || []).length;

  return {
    stats: {
      lines,
      objects,
      traits,
      functions,
      characters: code.length,
    },
    complexity: {
      score: Math.min(10, Math.round((objects + traits + functions) / 5)),
      level:
        objects + traits + functions < 10
          ? 'simple'
          : objects + traits + functions < 30
            ? 'moderate'
            : 'complex',
    },
    suggestions: [
      ...(traits === 0 ? ['Consider adding VR traits for interactivity'] : []),
      ...(objects > 20 ? ['Consider using templates to reduce duplication'] : []),
      ...(lines > 200 ? ['Consider splitting into multiple composition files'] : []),
    ],
  };
}

// === TEXT-TO-3D HANDLER ===

interface NativeAutoRigEnrichmentInput {
  holoCode: string;
  description: string;
  provider: string;
  modelFilePath: string;
  objectName?: string;
  traits?: string[];
  rig: AutoRigRigType;
  pose: AutoRigPose;
}

interface NativeAutoRigEnrichment {
  holoCode: string;
  traits: string[];
  autoRig: NativeAutoRigPlan;
  generatedMesh: {
    model: string;
    provider: string;
    description: string;
    topology: string;
  };
  skeleton: {
    rig: AutoRigRigType;
    pose: AutoRigPose;
    boneCount: number;
    standard: string;
  };
}

function normalizeGenerate3DObjectRig(value: unknown): AutoRigRigType | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (value === 'humanoid' || value === 'custom') return value;
  throw new Error('rig must be one of: humanoid, custom');
}

function normalizeGenerate3DObjectPose(value: unknown): AutoRigPose {
  if (value === undefined || value === null || value === '') return 't-pose';
  if (value === 't-pose' || value === 'a-pose') return value;
  throw new Error('pose must be one of: t-pose, a-pose');
}

function basenameFromPath(filePath: string): string {
  return filePath.split(/[\\/]/).filter(Boolean).pop() || 'generated.glb';
}

function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeHoloString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function quoteHolo(value: string): string {
  return `"${escapeHoloString(value)}"`;
}

function sanitizeGeneratedObjectName(description: string): string {
  return (
    description
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 40) || 'generated_object'
  );
}

function extractFirstHoloObjectName(holoCode: string): string | undefined {
  return holoCode.match(/object\s+"([^"]+)"/)?.[1];
}

function mergeGeneratedObjectTraits(traits: string[] | undefined): string[] {
  return Array.from(new Set([...(traits ?? []), 'generated_mesh', 'auto_rig', 'skeleton']));
}

function injectNativeAutoRigAnnotations(
  holoCode: string,
  objectName: string,
  modelFile: string,
  provider: string,
  plan: NativeAutoRigPlan
): string {
  const annotations = [
    `@generated_mesh(model: ${quoteHolo(modelFile)}, provider: ${quoteHolo(provider)}, topology: ${quoteHolo(plan.topology)})`,
    `@auto_rig(rig: ${quoteHolo(plan.rig)}, pose: ${quoteHolo(plan.pose)}, source: ${quoteHolo(plan.source)})`,
    `@skeleton(rig: ${quoteHolo(plan.rig)}, pose: ${quoteHolo(plan.pose)}, bone_count: ${plan.boneCount}, standard: ${quoteHolo(plan.animationCompatibility.standard)})`,
  ];
  const missingAnnotations = annotations.filter((annotation) => {
    const name = annotation.slice(0, annotation.indexOf('('));
    return !holoCode.includes(name);
  });

  if (missingAnnotations.length === 0) return holoCode;

  const objectPattern = new RegExp(`(object\\s+"${escapeRegexLiteral(objectName)}"[^\\{]*)(\\{)`);
  if (objectPattern.test(holoCode)) {
    return holoCode.replace(objectPattern, `$1 ${missingAnnotations.join(' ')} $2`);
  }

  return `composition "${escapeHoloString(objectName)}" {
  object "${escapeHoloString(objectName)}" ${missingAnnotations.join(' ')} {
    model: "${escapeHoloString(modelFile)}"
  }
}
`;
}

export function applyNativeAutoRigToGeneratedObject(
  input: NativeAutoRigEnrichmentInput
): NativeAutoRigEnrichment {
  const objectName =
    input.objectName ??
    extractFirstHoloObjectName(input.holoCode) ??
    sanitizeGeneratedObjectName(input.description);
  const modelFile = basenameFromPath(input.modelFilePath);
  const autoRig = createNativeAutoRigPlan({
    rig: input.rig,
    pose: input.pose,
    sourceMesh: input.modelFilePath,
    objectName,
    provider: input.provider,
    topology: 'animation-compatible',
  });

  return {
    holoCode: injectNativeAutoRigAnnotations(
      input.holoCode,
      objectName,
      modelFile,
      input.provider,
      autoRig
    ),
    traits: mergeGeneratedObjectTraits(input.traits),
    autoRig,
    generatedMesh: {
      model: modelFile,
      provider: input.provider,
      description: input.description,
      topology: autoRig.topology,
    },
    skeleton: {
      rig: autoRig.rig,
      pose: autoRig.pose,
      boneCount: autoRig.boneCount,
      standard: autoRig.animationCompatibility.standard,
    },
  };
}

async function handleGenerate3DObject(args: Record<string, unknown>) {
  const description = args.description as string;
  const providerName = (args.provider as string) || 'meshy';
  type TextTo3DStyle = 'realistic' | 'cartoon' | 'low-poly' | 'pbr';
  const style = ((args.style as string) || 'pbr') as TextTo3DStyle;
  const objectName = args.objectName as string | undefined;
  let rig: AutoRigRigType | undefined;
  let pose: AutoRigPose;

  if (!description) {
    return { success: false, error: 'description is required' };
  }

  try {
    rig = normalizeGenerate3DObjectRig(args.rig);
    pose = normalizeGenerate3DObjectPose(args.pose);
  } catch (error: unknown) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }

  try {
    // Dynamic import to avoid loading heavy deps when not needed
    const { MeshyProvider, TripoProvider, textTo3DToHolo } = await import(
      '../../cli/src/importers/text-to-3d-importer' as string
    );

    let provider;
    if (providerName === 'tripo') {
      provider = new TripoProvider();
    } else {
      provider = new MeshyProvider();
    }

    const result = await textTo3DToHolo({
      description,
      provider,
      style,
      objectName,
    });
    const autoRigEnrichment = rig
      ? applyNativeAutoRigToGeneratedObject({
          holoCode: result.holoCode,
          description,
          provider: result.metadata.provider,
          modelFilePath: result.modelFilePath,
          objectName,
          traits: result.traits,
          rig,
          pose,
        })
      : undefined;

    return {
      success: true,
      holoCode: autoRigEnrichment?.holoCode ?? result.holoCode,
      holoFilePath: result.holoFilePath,
      modelFilePath: result.modelFilePath,
      traits: autoRigEnrichment?.traits ?? result.traits,
      provider: result.metadata.provider,
      generationTimeMs: result.metadata.generationTimeMs,
      generatedMesh: autoRigEnrichment?.generatedMesh,
      autoRig: autoRigEnrichment?.autoRig,
      skeleton: autoRigEnrichment?.skeleton,
    };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    // Check for missing API key
    if (msg.includes('API_KEY required')) {
      return {
        success: false,
        error: msg,
        hint: 'Set MESHY_API_KEY or TRIPO_API_KEY in your environment',
      };
    }
    return {
      success: false,
      error: msg || 'Text-to-3D generation failed',
    };
  }
}

// === HOLOLAND TRAINING DATA HANDLER ===

async function handleGenerateHololandTraining(args: Record<string, unknown>) {
  const variationsPerExample = (args.variations_per_example as number) ?? 4;
  const categoryFilter = (args.category as string) ?? 'all';
  const outputFile = args.output_file as string | undefined;

  let examples = generateHololandDataset(variationsPerExample);

  if (categoryFilter !== 'all') {
    examples = examples.filter((e) => e.metadata.category === (categoryFilter as TrainingCategory));
  }

  const jsonl = datasetToJsonl(examples);

  if (outputFile) {
    const { writeFileSync } = await import('fs');
    const { resolve } = await import('path');
    const filePath = resolve(process.cwd(), outputFile);
    writeFileSync(filePath, jsonl, 'utf-8');
    return {
      success: true,
      examples_count: examples.length,
      categories: [...new Set(examples.map((e) => e.metadata.category))],
      file_path: filePath,
      file_size_bytes: Buffer.byteLength(jsonl, 'utf-8'),
      message: `Generated ${examples.length} training examples → ${filePath}`,
    };
  }

  return {
    success: true,
    examples_count: examples.length,
    categories: [...new Set(examples.map((e) => e.metadata.category))],
    jsonl,
    message: `Generated ${examples.length} training examples`,
  };
}
